import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { blogService } from '../../services/blog.service';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaGlobe, FaLock, FaEye, FaEyeSlash, FaCheck } from 'react-icons/fa';

const ManageBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogs();
    fetchStats();
  }, [filter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await blogService.getAllBlogs(params);
      setBlogs(data.blogs || []);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await blogService.getBlogStats();
      setStats(data.stats || {});
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handlePublish = async (blogId) => {
    if (!window.confirm('Publish this blog?')) return;
    
    try {
      await blogService.publishBlog(blogId, false);
      toast.success('Blog published!');
      fetchBlogs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to publish blog');
    }
  };

  const handleToggleVisibility = async (blog) => {
    const newVisibility = !blog.isPublic;
    const message = newVisibility 
      ? 'Make this blog PUBLIC (visible without login)?' 
      : 'Make this blog INTERNAL (login required)?';
    
    if (!window.confirm(message)) return;
    
    try {
      await blogService.updateBlog(blog._id, { isPublic: newVisibility });
      toast.success(`Blog is now ${newVisibility ? 'PUBLIC' : 'INTERNAL'}`);
      fetchBlogs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleUnpublish = async (blogId) => {
    if (!window.confirm('Unpublish this blog? It will become a draft.')) return;
    
    try {
      await blogService.updateBlog(blogId, { 
        status: 'draft',
        publishedAt: null 
      });
      toast.success('Blog unpublished');
      fetchBlogs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to unpublish blog');
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure? This action cannot be undone!')) return;
    
    try {
      await blogService.deleteBlog(blogId);
      toast.success('Blog deleted');
      fetchBlogs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      published: 'bg-blue-100 text-blue-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Manage All Blogs</h1>
        <p className="text-gray-600 mt-2">Control blog publishing and visibility</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Total Blogs</p>
          <p className="text-3xl font-bold text-blue-600 mt-2">{stats.total || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Pending</p>
          <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Approved</p>
          <p className="text-3xl font-bold text-green-600 mt-2">{stats.approved || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Published</p>
          <p className="text-3xl font-bold text-blue-600 mt-2">{stats.published || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-1">
            <FaGlobe className="text-green-600 text-sm" />
            <p className="text-gray-500 text-sm">Public</p>
          </div>
          <p className="text-3xl font-bold text-green-600 mt-2">{stats.publicBlogs || 0}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg ${filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100'}`}
          >
            Pending ({stats.pending || 0})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg ${filter === 'approved' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}
          >
            Approved ({stats.approved || 0})
          </button>
          <button
            onClick={() => setFilter('published')}
            className={`px-4 py-2 rounded-lg ${filter === 'published' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            Published ({stats.published || 0})
          </button>
        </div>
      </div>

      {/* Blogs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : blogs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No blogs found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visibility</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {blogs.map((blog) => (
                  <tr key={blog._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <p className="font-medium truncate">{blog.title}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(blog.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{blog.authorName}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded capitalize">
                        {blog.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded capitalize ${getStatusBadge(blog.status)}`}>
                        {blog.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleVisibility(blog)}
                        disabled={blog.status !== 'published'}
                        className={`flex items-center gap-1 px-3 py-1 text-xs rounded-full ${
                          blog.isPublic 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={blog.status === 'published' ? 'Click to toggle' : 'Publish first'}
                      >
                        {blog.isPublic ? <FaGlobe /> : <FaLock />}
                        {blog.isPublic ? 'Public' : 'Internal'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex gap-3">
                        <span title="Views">{blog.views || 0} 👁️</span>
                        <span title="Likes">{blog.likes?.length || 0} ❤️</span>
                        <span title="Comments">{blog.comments?.length || 0} 💬</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {/* View */}
                        <button
                          onClick={() => navigate(`/dashboard/blogs/view/${blog._id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="View"
                        >
                          <FaEye />
                        </button>

                        {/* Publish (only if approved) */}
                        {blog.status === 'approved' && (
                          <button
                            onClick={() => handlePublish(blog._id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Publish"
                          >
                            <FaCheck />
                          </button>
                        )}

                        {/* Unpublish (only if published) */}
                        {blog.status === 'published' && (
                          <button
                            onClick={() => handleUnpublish(blog._id)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                            title="Unpublish"
                          >
                            <FaEyeSlash />
                          </button>
                        )}

                        {/* Edit */}
                        <button
                          onClick={() => navigate(`/dashboard/blogs/edit/${blog._id}`)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(blog._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBlogs;
